import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { FixedAside } from "@/components/_templates/fixed-aside/fixed-aside";
import { SheetHeader } from "@/components/_templates/sheet/sheet-header";
import { SheetMainContent } from "@/components/_templates/sheet/sheet-main-content";
import { DataSourceSelectDropdown } from "@/domain/emission-factor/components/data-source-select-dropdown";
import { RightAsideRowInput } from "@/domain/energy-source/components/data-grid-right-aside/data-source/right-aside-row-input";
import { RightAsideRow } from "@/domain/energy-source/components/data-grid-right-aside/right-aside-row";
import {
  CUSTOM_DATA_SOURCE_PREFIX,
  DataSource,
  ENERGY_DATA_SOURCE_LIST,
} from "@/domain/energy-source/lib/data-source-quality-mapping";
import { ReferenceYearField } from "@/domain/inventory/components/sector-tabs/reference-year-field";
import { useCurrentInventoryContext } from "@/domain/inventory/hooks/use-current-inventory-context";
import { isMunicipalPropertyGhgDerived } from "@/domain/inventory/lib/is-municipal-property-ghg-derived";
import { GhgQuality } from "@ark/shared";

import { GhgQualitySelect } from "./ghg-quality-select";
import { GhgInputQualityValues, useGhgInputMutations } from "./use-ghg-input-mutations";

export interface GhgInputCell {
  energySourceId: string;
  energySourceTitle: string;
  subsectorId: string;
  subsectorTitle: string;
  subsubsectorId: string | null;
  sectorId: string;
  scope: number | null;
  amount: number;
  displayValue: string;
  unit: string;
  gpcQualityRating: GhgQuality | null;
  referenceYear: number | null;
  dataSource: string | null;
  inventoryValueId: string | null;
  isAggregate?: boolean;
  isReadOnly?: boolean;
  subsectorKind?: string | null;
}

interface GhgInputRightAsideProps {
  selectedCell: GhgInputCell | null;
  isEditing: boolean;
  dataSourceList?: DataSource[];
  onDataSourceChange?: (dataSource: string | null) => void;
  onQualityChange?: (quality: GhgQuality) => void;
}

export const GhgInputRightAside: FC<GhgInputRightAsideProps> = ({
  selectedCell,
  isEditing,
  dataSourceList = ENERGY_DATA_SOURCE_LIST,
  onDataSourceChange,
  onQualityChange,
}) => {
  const { t } = useTranslation("inventory");
  const { currentInventory } = useCurrentInventoryContext();
  const { writeCellQuality } = useGhgInputMutations();

  if (!selectedCell) {
    return (
      <FixedAside>
        <SheetHeader>{t("rightAside.selectedCell")}</SheetHeader>
        <SheetMainContent>
          <div className="pl-2 text-sm text-muted-foreground">{t("gpcInputRightAside.noSelection")}</div>
        </SheetMainContent>
      </FixedAside>
    );
  }
  const editable: boolean =
    isEditing && !selectedCell.isReadOnly && !selectedCell.isAggregate && !isMunicipalPropertyGhgDerived(selectedCell.inventoryValueId ?? "");

  const persist = (next: GhgInputQualityValues) => {
    if (!editable) return;
    writeCellQuality(
      {
        energySourceId: selectedCell.energySourceId,
        sectorId: selectedCell.sectorId,
        subsectorId: selectedCell.subsectorId,
        subsubsectorId: selectedCell.subsubsectorId,
        scope: selectedCell.scope,
        amount: selectedCell.amount,
        existingId: selectedCell.inventoryValueId ?? undefined,
      },
      next,
    );
  };

  const handleQualityChange = (quality: GhgQuality) => {
    if (onQualityChange) {
      if (isEditing) onQualityChange(quality);
      return;
    }
    persist({
      gpcQualityRating: quality,
      referenceYear: selectedCell.referenceYear,
      dataSource: selectedCell.dataSource,
    });
  };

  const handleReferenceYearChange = (nextYear: number | null) => {
    if (nextYear === selectedCell.referenceYear) return;
    persist({
      gpcQualityRating: selectedCell.gpcQualityRating,
      referenceYear: nextYear,
      dataSource: selectedCell.dataSource,
    });
  };

  const persistDataSource = (dataSource: string | null) => {
    if (onDataSourceChange) {
      if (isEditing) onDataSourceChange(dataSource);
      return;
    }
    persist({
      gpcQualityRating: selectedCell.gpcQualityRating,
      referenceYear: selectedCell.referenceYear,
      dataSource,
    });
  };

  const handleDataSourceSelect = (dataSource: Pick<DataSource, "id" | "title"> | null) => {
    if (!dataSource) {
      persistDataSource(null);
      return;
    }
    if (dataSource.title === CUSTOM_DATA_SOURCE_PREFIX) {
      persistDataSource(
        selectedCell.dataSource?.startsWith(`${CUSTOM_DATA_SOURCE_PREFIX}: `)
          ? selectedCell.dataSource
          : CUSTOM_DATA_SOURCE_PREFIX,
      );
      return;
    }
    persistDataSource(dataSource.title);
  };

  const handleCustomSourceChange = (raw: string) => {
    const trimmed = raw.trim();
    persistDataSource(trimmed ? `${CUSTOM_DATA_SOURCE_PREFIX}: ${trimmed}` : CUSTOM_DATA_SOURCE_PREFIX);
  };

  const selectedDataSourceOption =
    selectedCell.dataSource === null
      ? null
      : selectedCell.dataSource.startsWith(CUSTOM_DATA_SOURCE_PREFIX)
        ? (dataSourceList.find((ds) => ds.title === CUSTOM_DATA_SOURCE_PREFIX) ?? null)
        : (dataSourceList.find((ds) => ds.title === selectedCell.dataSource) ?? null);
  const isCustomSource = selectedDataSourceOption?.title === CUSTOM_DATA_SOURCE_PREFIX;

  return (
    <FixedAside>
      <SheetHeader>{t("rightAside.selectedCell")}</SheetHeader>
      <SheetMainContent className="max-h-[calc(100vh-100px)]">
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col pl-2 text-sm text-muted-foreground">
            <RightAsideRow label={t("rightAside.energySource")} value={selectedCell.energySourceTitle} />
          </div>
          <div className="flex flex-col pl-2 text-sm text-muted-foreground">
            <RightAsideRow label={t("rightAside.subsector")} value={selectedCell.subsectorTitle} />
          </div>
          <div className="flex flex-row items-start gap-3 overflow-x-hidden pl-2 text-sm text-muted-foreground">
            <div className="mt-2 w-24 shrink-0 font-medium">{t("rightAside.value")}</div>
            <span className="mt-2 block min-w-0 flex-1 truncate text-sm">
              {`${selectedCell.displayValue} ${selectedCell.unit}`}
            </span>
          </div>
          <div className="flex flex-row items-start gap-3 pl-2 text-sm text-muted-foreground">
            <div className="mt-2 w-24 shrink-0 font-medium">{t("rightAside.dataQuality")}</div>
            {editable || (isEditing && onQualityChange) ? (
              <GhgQualitySelect
                key={`quality-${selectedCell.energySourceId}-${selectedCell.subsectorId}-${selectedCell.subsubsectorId ?? ""}-${selectedCell.scope ?? ""}`}
                value={selectedCell.gpcQualityRating}
                onValueChange={handleQualityChange}
              />
            ) : (
              <span className="mt-2 block flex-1 truncate text-sm">
                {selectedCell.gpcQualityRating
                  ? t(`gpcEmissionFactorRightAside.quality.${selectedCell.gpcQualityRating}`)
                  : "-"}
              </span>
            )}
          </div>
          {!selectedCell.isAggregate && (
            <>
              <ReferenceYearField
                referenceYear={selectedCell.referenceYear}
                inventoryYear={currentInventory.year}
                cellKey={`${selectedCell.energySourceId}-${selectedCell.subsectorId}-${selectedCell.subsubsectorId ?? ""}-${selectedCell.scope ?? ""}`}
                isEditing={editable}
                onChange={handleReferenceYearChange}
              />
              <div className="flex flex-row items-start gap-3 pl-2 text-sm text-muted-foreground">
                <div className="mt-2 w-24 shrink-0 font-medium">{t("rightAside.source")}</div>
                {editable || (isEditing && onDataSourceChange) ? (
                  <div className="mt-1 flex min-w-0 flex-1 flex-col gap-2">
                    <DataSourceSelectDropdown
                      onSelect={handleDataSourceSelect}
                      selectedOption={selectedDataSourceOption}
                      dataSourceList={dataSourceList}
                      asChild
                      isReadOnly={false}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-auto min-h-9 w-full justify-start whitespace-normal py-1.5 text-left text-xs font-light"
                      >
                        <span className="block w-full break-words">
                          {selectedDataSourceOption?.title ??
                            selectedCell.dataSource ??
                            t("gpcEmissionFactorRightAside.sourcePlaceholder")}
                        </span>
                      </Button>
                    </DataSourceSelectDropdown>
                    {isCustomSource && (
                      <RightAsideRowInput
                        key={`source-custom-${selectedCell.energySourceId}-${selectedCell.subsectorId}-${selectedCell.subsubsectorId ?? ""}-${selectedCell.scope ?? ""}`}
                        inventoryValueId={selectedCell.inventoryValueId ?? ""}
                        dataSource={onDataSourceChange ? selectedCell.dataSource : undefined}
                        placeholder={t("gpcEmissionFactorRightAside.sourcePlaceholder")}
                        onValueChange={handleCustomSourceChange}
                      />
                    )}
                  </div>
                ) : (
                  <span className="mt-2 block flex-1 truncate text-sm">{selectedCell.dataSource ?? "-"}</span>
                )}
              </div>
            </>
          )}
        </div>
      </SheetMainContent>
    </FixedAside>
  );
};
